import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useState, useEffect } from 'react'
import { Link, useLocation } from 'wouter'
import { ArrowLeft, Sparkles, Save, Eye, Crown, AlertCircle } from 'lucide-react'
import { SubscriptionModal } from '@/components/subscription-modal'
import { AIAssistant } from '@/components/ai-assistant'
import { FileUpload } from '@/components/file-upload'
import { buildApiUrl } from '@/config/api'

import toast from 'react-hot-toast'

export function CreateRfp() {

  const [, setLocation] = useLocation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    hasActiveSubscription: boolean
    subscriptionStatus: string
    subscriptionPlan: string
  } | null>(null)
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true)
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: [''],
    budgetMin: '',
    budgetMax: '',
    budgetCurrency: 'USD',
    deadline: '',
    category: '',
    workType: 'REMOTE' as 'REMOTE' | 'ONSITE' | 'HYBRID',
    country: '',
    externalLinks: [''],
  })

  // const [isGenerating, setIsGenerating] = useState(false)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [showAIAssistant, setShowAIAssistant] = useState(false)
  const [attachments, setAttachments] = useState<any[]>([])
  const [createdRfpId, setCreatedRfpId] = useState<string | null>(null)

  // Check subscription status on component mount
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          setIsCheckingSubscription(false)
          return
        }

        const response = await fetch(buildApiUrl('/api/subscription/status'), {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })

        if (response.ok) {
          const data = await response.json()
          setSubscriptionStatus(data.data)
        }
      } catch (error) {
        console.error('Error checking subscription status:', error)
      } finally {
        setIsCheckingSubscription(false)
      }
    }

    checkSubscriptionStatus()
  }, [])

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleBudgetChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const addRequirement = () => {
    setFormData(prev => ({
      ...prev,
      requirements: [...prev.requirements, '']
    }))
  }

  const removeRequirement = (index: number) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }))
  }

  const updateRequirement = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.map((req, i) => i === index ? value : req)
    }))
  }

  // External Links management functions
  const addExternalLink = () => {
    setFormData(prev => ({
      ...prev,
      externalLinks: [...prev.externalLinks, '']
    }))
  }

  const removeExternalLink = (index: number) => {
    setFormData(prev => ({
      ...prev,
      externalLinks: prev.externalLinks.filter((_, i) => i !== index)
    }))
  }

  const updateExternalLink = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      externalLinks: prev.externalLinks.map((link, i) => i === index ? value : link)
    }))
  }

  // URL validation helper
  const isValidUrl = (url: string) => {
    if (!url.trim()) return true // Empty is valid (optional)
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const handleAIContentGenerated = (content: string) => {
    // Parse the AI-generated content and update form fields
    const lines = content.split('\n')
    let currentSection = ''
    let description = ''
    let requirements: string[] = []
    
    for (const line of lines) {
      const trimmedLine = line.trim()
      if (!trimmedLine) continue
      
      // Detect sections
      if (trimmedLine.toLowerCase().includes('executive summary') || 
          trimmedLine.toLowerCase().includes('project overview')) {
        currentSection = 'description'
        continue
      }
      
      if (trimmedLine.toLowerCase().includes('detailed requirements') ||
          trimmedLine.toLowerCase().includes('technical specifications')) {
        currentSection = 'requirements'
        continue
      }
      
      // Extract content based on section
      if (currentSection === 'description' && trimmedLine.length > 10) {
        description += (description ? '\n' : '') + trimmedLine
      } else if (currentSection === 'requirements' && trimmedLine.length > 5) {
        if (trimmedLine.match(/^\d+\./)) {
          requirements.push(trimmedLine.replace(/^\d+\.\s*/, ''))
        } else if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-')) {
          requirements.push(trimmedLine.replace(/^[•-]\s*/, ''))
        }
      }
    }
    
    // Update form with AI-generated content
    setFormData(prev => ({
      ...prev,
      description: description || prev.description,
      requirements: requirements.length > 0 ? requirements : prev.requirements
    }))
    
    setShowAIAssistant(false)
    toast.success('AI content applied to form!')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Validate form data (matching backend validation)
      if (!formData.title.trim()) {
        toast.error('Title is required (minimum 1 character)')
        return
      }
      if (formData.description.trim().length < 10) {
        toast.error('Description must be at least 10 characters long')
        return
      }
      if (!formData.category.trim()) {
        toast.error('Category is required (minimum 1 character)')
        return
      }
      if (!formData.deadline) {
        toast.error('Deadline is required')
        return
      }
      if (!formData.budgetMin || parseFloat(formData.budgetMin) <= 0) {
        toast.error('Minimum budget must be greater than 0')
        return
      }
      if (!formData.budgetMax || parseFloat(formData.budgetMax) <= 0) {
        toast.error('Maximum budget must be greater than 0')
        return
      }
      if (parseFloat(formData.budgetMin) >= parseFloat(formData.budgetMax)) {
        toast.error('Maximum budget must be greater than minimum budget')
        return
      }
      if (formData.requirements.length === 0 || formData.requirements.every(req => !req.trim())) {
        toast.error('At least one requirement is needed (minimum 1 character each)')
        return
      }
      // Check each requirement has at least 1 character
      for (let i = 0; i < formData.requirements.length; i++) {
        if (!formData.requirements[i].trim()) {
          toast.error(`Requirement ${i + 1} must have at least 1 character`)
          return
        }
      }
      // Validate external links
      for (let i = 0; i < formData.externalLinks.length; i++) {
        const link = formData.externalLinks[i].trim()
        if (link && !isValidUrl(link)) {
          toast.error(`External link ${i + 1} is not a valid URL`)
          return
        }
      }

      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Please log in to create an RFP')
        return
      }

      // Prepare data for API
      const rfpData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        requirements: formData.requirements.filter(req => req.trim()),
        budget: {
          min: parseFloat(formData.budgetMin),
          max: parseFloat(formData.budgetMax),
          currency: formData.budgetCurrency,
        },
        deadline: formData.deadline,
        category: formData.category.trim(),
        workType: formData.workType,
        country: formData.country.trim() || undefined,
        attachments: attachments.map(att => att.key), // Include file attachment keys
        externalLinks: formData.externalLinks.filter(link => link.trim()), // Include external links
      }

      const response = await fetch(buildApiUrl('/api/rfps'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(rfpData),
      })

      const data = await response.json()

      if (data.success) {
        // Set the created RFP ID for file uploads
        if (data.data?.id) {
          setCreatedRfpId(data.data.id)
        }
        toast.success('RFP created successfully!')
        setLocation('/my-rfps') // Redirect to My RFPs page
      } else if (data.error === 'Paid subscription required' || response.status === 403) {
        console.log('Subscription required, showing modal')
        setShowSubscriptionModal(true)
      } else if (response.status === 400) {
        // Validation error
        if (data.details && Array.isArray(data.details)) {
          const errorMessages = data.details.map((detail: any) => detail.message).join(', ')
          toast.error(`Validation error: ${errorMessages}`)
        } else {
          toast.error(data.error || 'Please check your input and try again')
        }
      } else if (response.status === 401) {
        toast.error('Please log in to create an RFP')
      } else {
        console.error('RFP creation error:', data)
        toast.error(data.error || 'Unable to create RFP. Please try again.')
      }
    } catch (error) {
      console.error('Error creating RFP:', error)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        toast.error('Unable to connect to the server. Please check your internet connection and try again.')
      } else {
        toast.error('An unexpected error occurred. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link href="/request-for-proposal-marketplace">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Create RFP</h1>
            <p className="text-muted-foreground">
              Create a professional Request for Proposal with AI assistance.
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline">
            <Eye className="mr-2 h-4 w-4" />
            Preview
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            <Save className="mr-2 h-4 w-4" />
            {isSubmitting ? 'Creating...' : 'Save Draft'}
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {isCheckingSubscription && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <p className="text-blue-700 text-sm">Checking subscription status...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscription Success */}
      {!isCheckingSubscription && subscriptionStatus && subscriptionStatus.hasActiveSubscription && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-3">
              <Crown className="h-5 w-5 text-green-600" />
              <div>
                <h3 className="font-semibold text-green-800">PRO Subscription Active</h3>
                <p className="text-green-700 text-sm">You can create and publish RFPs with your PRO plan.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscription Warning */}
      {!isCheckingSubscription && subscriptionStatus && !subscriptionStatus.hasActiveSubscription && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800 mb-2">PRO Subscription Required</h3>
                <p className="text-amber-700 text-sm mb-3">
                  Creating RFPs requires a PRO subscription. You currently have a {subscriptionStatus.subscriptionStatus} plan.
                </p>
                <Button 
                  onClick={() => setShowSubscriptionModal(true)}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Crown className="mr-2 h-4 w-4" />
                  Upgrade to PRO
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="relative">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Disable form if no active subscription */}
          {!isCheckingSubscription && subscriptionStatus && !subscriptionStatus.hasActiveSubscription && (
            <div className="absolute inset-0 bg-white/50 backdrop-blur-sm z-10 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <Crown className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">PRO Subscription Required</h3>
                <p className="text-gray-600 mb-4">Please upgrade to PRO to create RFPs</p>
                <Button 
                  onClick={() => setShowSubscriptionModal(true)}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Crown className="mr-2 h-4 w-4" />
                  Upgrade to PRO
                </Button>
              </div>
            </div>
          )}
          <div className={!isCheckingSubscription && subscriptionStatus && !subscriptionStatus.hasActiveSubscription ? 'pointer-events-none opacity-50' : ''}>
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>
              Provide the essential details for your RFP.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter RFP title (minimum 1 character)..."
                  className="flex-1 px-3 py-2 border border-input rounded-md bg-background"
                  required
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAIAssistant(true)}
                >
                  <Sparkles className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
                required
              >
                <option value="">Select a category</option>
                <option value="Technology">Technology</option>
                <option value="Marketing">Marketing</option>
                <option value="Consulting">Consulting</option>
                <option value="Design">Design</option>
                <option value="Operations">Operations</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Work Type</label>
                <select
                  value={formData.workType}
                  onChange={(e) => handleInputChange('workType', e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background"
                  required
                >
                  <option value="REMOTE">Remote</option>
                  <option value="ONSITE">On-site</option>
                  <option value="HYBRID">Hybrid</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Country (Optional)</label>
                <select
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background"
                >
                  <option value="">Select a country</option>
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Germany">Germany</option>
                  <option value="France">France</option>
                  <option value="Italy">Italy</option>
                  <option value="Spain">Spain</option>
                  <option value="Netherlands">Netherlands</option>
                  <option value="Belgium">Belgium</option>
                  <option value="Switzerland">Switzerland</option>
                  <option value="Austria">Austria</option>
                  <option value="Sweden">Sweden</option>
                  <option value="Norway">Norway</option>
                  <option value="Denmark">Denmark</option>
                  <option value="Finland">Finland</option>
                  <option value="Poland">Poland</option>
                  <option value="Czech Republic">Czech Republic</option>
                  <option value="Hungary">Hungary</option>
                  <option value="Slovakia">Slovakia</option>
                  <option value="Slovenia">Slovenia</option>
                  <option value="Croatia">Croatia</option>
                  <option value="Serbia">Serbia</option>
                  <option value="Bulgaria">Bulgaria</option>
                  <option value="Romania">Romania</option>
                  <option value="Greece">Greece</option>
                  <option value="Portugal">Portugal</option>
                  <option value="Ireland">Ireland</option>
                  <option value="Iceland">Iceland</option>
                  <option value="Luxembourg">Luxembourg</option>
                  <option value="Malta">Malta</option>
                  <option value="Cyprus">Cyprus</option>
                  <option value="Estonia">Estonia</option>
                  <option value="Latvia">Latvia</option>
                  <option value="Lithuania">Lithuania</option>
                  <option value="Australia">Australia</option>
                  <option value="New Zealand">New Zealand</option>
                  <option value="Japan">Japan</option>
                  <option value="South Korea">South Korea</option>
                  <option value="China">China</option>
                  <option value="India">India</option>
                  <option value="Singapore">Singapore</option>
                  <option value="Malaysia">Malaysia</option>
                  <option value="Thailand">Thailand</option>
                  <option value="Vietnam">Vietnam</option>
                  <option value="Philippines">Philippines</option>
                  <option value="Indonesia">Indonesia</option>
                  <option value="Brazil">Brazil</option>
                  <option value="Argentina">Argentina</option>
                  <option value="Chile">Chile</option>
                  <option value="Colombia">Colombia</option>
                  <option value="Peru">Peru</option>
                  <option value="Mexico">Mexico</option>
                  <option value="South Africa">South Africa</option>
                  <option value="Egypt">Egypt</option>
                  <option value="Morocco">Morocco</option>
                  <option value="Tunisia">Tunisia</option>
                  <option value="Algeria">Algeria</option>
                  <option value="Nigeria">Nigeria</option>
                  <option value="Kenya">Kenya</option>
                  <option value="Ghana">Ghana</option>
                  <option value="Ethiopia">Ethiopia</option>
                  <option value="Uganda">Uganda</option>
                  <option value="Tanzania">Tanzania</option>
                  <option value="Israel">Israel</option>
                  <option value="Turkey">Turkey</option>
                  <option value="Saudi Arabia">Saudi Arabia</option>
                  <option value="United Arab Emirates">United Arab Emirates</option>
                  <option value="Qatar">Qatar</option>
                  <option value="Kuwait">Kuwait</option>
                  <option value="Bahrain">Bahrain</option>
                  <option value="Oman">Oman</option>
                  <option value="Jordan">Jordan</option>
                  <option value="Lebanon">Lebanon</option>
                  <option value="Iran">Iran</option>
                  <option value="Iraq">Iraq</option>
                  <option value="Pakistan">Pakistan</option>
                  <option value="Bangladesh">Bangladesh</option>
                  <option value="Sri Lanka">Sri Lanka</option>
                  <option value="Nepal">Nepal</option>
                  <option value="Bhutan">Bhutan</option>
                  <option value="Myanmar">Myanmar</option>
                  <option value="Cambodia">Cambodia</option>
                  <option value="Laos">Laos</option>
                  <option value="Mongolia">Mongolia</option>
                  <option value="Kazakhstan">Kazakhstan</option>
                  <option value="Uzbekistan">Uzbekistan</option>
                  <option value="Kyrgyzstan">Kyrgyzstan</option>
                  <option value="Tajikistan">Tajikistan</option>
                  <option value="Turkmenistan">Turkmenistan</option>
                  <option value="Azerbaijan">Azerbaijan</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Armenia">Armenia</option>
                  <option value="Ukraine">Ukraine</option>
                  <option value="Belarus">Belarus</option>
                  <option value="Moldova">Moldova</option>
                  <option value="Russia">Russia</option>
                  <option value="Venezuela">Venezuela</option>
                  <option value="Ecuador">Ecuador</option>
                  <option value="Bolivia">Bolivia</option>
                  <option value="Paraguay">Paraguay</option>
                  <option value="Uruguay">Uruguay</option>
                  <option value="Guyana">Guyana</option>
                  <option value="Suriname">Suriname</option>
                  <option value="French Guiana">French Guiana</option>
                  <option value="Costa Rica">Costa Rica</option>
                  <option value="Panama">Panama</option>
                  <option value="Nicaragua">Nicaragua</option>
                  <option value="Honduras">Honduras</option>
                  <option value="El Salvador">El Salvador</option>
                  <option value="Guatemala">Guatemala</option>
                  <option value="Belize">Belize</option>
                  <option value="Cuba">Cuba</option>
                  <option value="Jamaica">Jamaica</option>
                  <option value="Haiti">Haiti</option>
                  <option value="Dominican Republic">Dominican Republic</option>
                  <option value="Puerto Rico">Puerto Rico</option>
                  <option value="Trinidad and Tobago">Trinidad and Tobago</option>
                  <option value="Barbados">Barbados</option>
                  <option value="Bahamas">Bahamas</option>
                  <option value="Antigua and Barbuda">Antigua and Barbuda</option>
                  <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
                  <option value="Saint Lucia">Saint Lucia</option>
                  <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</option>
                  <option value="Grenada">Grenada</option>
                  <option value="Dominica">Dominica</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Description */}
        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
            <CardDescription>
              Provide a detailed description of what you're looking for.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex space-x-2">
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe your project requirements, goals, and expectations (minimum 10 characters)..."
                className="flex-1 px-3 py-2 border border-input rounded-md bg-background min-h-[120px] resize-y"
                required
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAIAssistant(true)}
              >
                <Sparkles className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Requirements */}
        <Card>
          <CardHeader>
            <CardTitle>Requirements</CardTitle>
            <CardDescription>
              List the specific requirements and deliverables you expect.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.requirements.map((requirement, index) => (
              <div key={index} className="flex space-x-2">
                <input
                  type="text"
                  value={requirement}
                  onChange={(e) => updateRequirement(index, e.target.value)}
                  placeholder={`Requirement ${index + 1}...`}
                  className="flex-1 px-3 py-2 border border-input rounded-md bg-background"
                />
                {formData.requirements.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => removeRequirement(index)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="outline" onClick={addRequirement}>
              Add Requirement
            </Button>
          </CardContent>
        </Card>

        {/* File Attachments - PRO Feature */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <span>File Attachments</span>
              {subscriptionStatus?.subscriptionPlan !== 'PRO' && (
                <span className="ml-2 px-2 py-1 text-xs bg-amber-100 text-amber-800 rounded-full">
                  PRO
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Upload supporting documents, specifications, or reference materials for your RFP.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {subscriptionStatus?.subscriptionPlan === 'PRO' ? (
              <FileUpload
                rfpId={createdRfpId}
                initialAttachments={[]}
                onAttachmentsChange={(newAttachments) => setAttachments(newAttachments)}
              />
            ) : (
              <div className="text-center py-8 space-y-4">
                <div className="flex justify-center">
                  <div className="p-3 bg-amber-100 rounded-full">
                    <AlertCircle className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">
                    File Attachments Available with PRO
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Upgrade to PRO to attach supporting documents, specifications, and reference materials to your RFPs.
                  </p>
                  <Button onClick={() => setShowSubscriptionModal(true)}>
                    Upgrade to PRO
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* External Links */}
        <Card>
          <CardHeader>
            <CardTitle>External Links</CardTitle>
            <CardDescription>
              Add links to external documents, specifications, or reference materials.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.externalLinks.map((link, index) => (
              <div key={index} className="flex space-x-2">
                <div className="flex-1">
                  <input
                    type="url"
                    value={link}
                    onChange={(e) => updateExternalLink(index, e.target.value)}
                    placeholder={`https://example.com/document${index + 1}.pdf`}
                    className={`w-full px-3 py-2 border rounded-md bg-background ${
                      !isValidUrl(link) ? 'border-red-300' : 'border-input'
                    }`}
                  />
                  {!isValidUrl(link) && (
                    <p className="text-sm text-red-600 mt-1">
                      Please enter a valid URL
                    </p>
                  )}
                </div>
                {formData.externalLinks.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => removeExternalLink(index)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="outline" onClick={addExternalLink}>
              Add External Link
            </Button>
          </CardContent>
        </Card>

        {/* Budget & Timeline */}
        <Card>
          <CardHeader>
            <CardTitle>Budget & Timeline</CardTitle>
            <CardDescription>
              Set your budget range and project deadline.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Min Budget</label>
                <input
                  type="number"
                                      value={formData.budgetMin}
                                      onChange={(e) => handleBudgetChange('budgetMin', e.target.value)}
                  placeholder="1000"
                  className="w-full px-3 py-2 border border-input rounded-md bg-background"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Max Budget</label>
                <input
                  type="number"
                                      value={formData.budgetMax}
                                      onChange={(e) => handleBudgetChange('budgetMax', e.target.value)}
                  placeholder="5000"
                  className="w-full px-3 py-2 border border-input rounded-md bg-background"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Currency</label>
                <select
                                      value={formData.budgetCurrency}
                                      onChange={(e) => handleBudgetChange('budgetCurrency', e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Deadline</label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => handleInputChange('deadline', e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
                required
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end space-x-2">
          <Link href="/request-for-proposal-marketplace">
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </Link>
          <Button type="submit" disabled={isSubmitting}>
            <Save className="mr-2 h-4 w-4" />
            {isSubmitting ? 'Creating...' : 'Create RFP'}
          </Button>
        </div>
          </div>
        </form>
      </div>

      {/* AI Assistant Modal */}
      {showAIAssistant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">AI Assistant</h2>
                <Button
                  variant="ghost"
                  onClick={() => setShowAIAssistant(false)}
                >
                  ×
                </Button>
              </div>
              <AIAssistant
                type="rfp"
                onContentGenerated={handleAIContentGenerated}
                initialData={{
                  title: formData.title,
                  description: formData.description,
                  requirements: formData.requirements.join('\n'),
                  budget: formData.budgetMin && formData.budgetMax 
                    ? `${formData.budgetCurrency} ${formData.budgetMin} - ${formData.budgetMax}`
                    : undefined,
                  deadline: formData.deadline,
                  workType: formData.workType,
                  country: formData.country,
                }}
              />
            </div>
          </div>
        </div>
      )}

      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        feature="publish"
        currentPlan={subscriptionStatus?.subscriptionPlan as 'FREE' | 'PRO'}
        hasActiveSubscription={subscriptionStatus?.hasActiveSubscription || false}
      />
    </div>
  )
} 